import HttpFactory from "~/repository/factory";
import { isPrivateMaterialMediaUrl } from "~/utils/schoolMaterialMedia";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  EditorAnnotationPayload,
  EditorAnnotationRecord,
  EditorAnswerResponse,
  EditorAudioResponse,
  EditorClassroomResponse,
  EditorEntityId,
  EditorPermissionsResponse,
  EditorTeachingClassResponse,
} from "~/types/editor";

const appendId = (endpoint: string, id: EditorEntityId): string =>
  `${endpoint}${encodeURIComponent(String(id))}`;

class EditorModule extends HttpFactory {
  async fetchPrivateMedia(url: string): Promise<Blob> {
    if (!isPrivateMaterialMediaUrl(url)) {
      throw new Error("Endereço de arquivo privado inválido.");
    }
    return await this.get<Blob>(url, {}, { responseType: "blob" });
  }

  async fetchPdf(
    lessonId: EditorEntityId,
    teachingClassId?: EditorEntityId | null,
  ): Promise<ArrayBuffer> {
    return await this.getResponse<ArrayBuffer>(
      appendId(ENDPOINTS.EDITOR.LESSON_PDF, lessonId),
      { teaching_class_id: teachingClassId },
      { responseType: "arraybuffer" },
    ).then((response) => response.data);
  }

  async fetchLessonPermissions(
    lessonId: EditorEntityId,
    teachingClassId?: EditorEntityId | null,
  ): Promise<EditorPermissionsResponse> {
    return await this.get<EditorPermissionsResponse>(
      appendId(ENDPOINTS.EDITOR.LESSON_PERMISSIONS, lessonId),
      { teaching_class_id: teachingClassId },
    );
  }

  async fetchLessonAnnotations(
    lessonId: EditorEntityId,
    teachingClassId?: EditorEntityId | null,
  ): Promise<EditorAnnotationRecord[]> {
    return await this.get<EditorAnnotationRecord[]>(
      appendId(ENDPOINTS.EDITOR.LESSON_ANNOTATIONS, lessonId),
      { teaching_class_id: teachingClassId },
    );
  }

  async postLessonAnnotation(
    payload: EditorAnnotationPayload,
    lessonId: EditorEntityId,
  ): Promise<EditorAnnotationRecord> {
    return await this.post<EditorAnnotationRecord>(
      appendId(ENDPOINTS.EDITOR.LESSON_ANNOTATIONS, lessonId),
      payload,
    );
  }

  async deleteLessonAnnotation(
    annotationId: string,
    lessonId: EditorEntityId,
  ): Promise<void> {
    const endpoint = appendId(ENDPOINTS.EDITOR.LESSON_ANNOTATIONS, lessonId);

    return await this.delete<void>(
      `${endpoint}?annotation_id=${encodeURIComponent(annotationId)}`,
    );
  }

  async fetchLessonAudios(
    lessonId: EditorEntityId,
    teachingClassId?: EditorEntityId | null,
  ): Promise<EditorAudioResponse[]> {
    return await this.get<EditorAudioResponse[]>(
      ENDPOINTS.EDITOR.LESSON_AUDIOS.replace(
        "{lessonId}",
        encodeURIComponent(String(lessonId)),
      ),
      { teaching_class_id: teachingClassId },
    );
  }

  async postLessonAudio(
    lessonId: EditorEntityId,
    payload: FormData,
  ): Promise<EditorAudioResponse> {
    return await this.post<EditorAudioResponse>(
      ENDPOINTS.EDITOR.LESSON_AUDIOS.replace(
        "{lessonId}",
        encodeURIComponent(String(lessonId)),
      ),
      payload,
    );
  }

  async fetchTeachingClassData(
    teachingClassId: EditorEntityId,
  ): Promise<EditorTeachingClassResponse> {
    return await this.get<EditorTeachingClassResponse>(
      appendId(ENDPOINTS.CLASSROOM.TEACHING_CLASSES, teachingClassId),
    );
  }

  async fetchTeachingClassPermissions(
    teachingClassId: EditorEntityId,
  ): Promise<EditorPermissionsResponse> {
    return await this.get<EditorPermissionsResponse>(
      appendId(ENDPOINTS.EDITOR.TEACHING_CLASS_PERMISSIONS, teachingClassId),
    );
  }

  async fetchTeachingClassAnnotations(
    teachingClassId: EditorEntityId,
  ): Promise<EditorAnnotationRecord[]> {
    return await this.get<EditorAnnotationRecord[]>(
      appendId(ENDPOINTS.EDITOR.TEACHING_CLASS_ANNOTATIONS, teachingClassId),
    );
  }

  async postTeachingClassAnnotation(
    payload: EditorAnnotationPayload,
    teachingClassId: EditorEntityId,
  ): Promise<EditorAnnotationRecord> {
    return await this.post<EditorAnnotationRecord>(
      appendId(ENDPOINTS.EDITOR.TEACHING_CLASS_ANNOTATIONS, teachingClassId),
      payload,
    );
  }

  async deleteTeachingClassAnnotation(
    annotationId: string,
    teachingClassId: EditorEntityId,
  ): Promise<void> {
    const endpoint = appendId(
      ENDPOINTS.EDITOR.TEACHING_CLASS_ANNOTATIONS,
      teachingClassId,
    );

    return await this.delete<void>(
      `${endpoint}?annotation_id=${encodeURIComponent(annotationId)}`,
    );
  }

  async fetchAnswers(
    teachingClassId: EditorEntityId,
  ): Promise<EditorAnswerResponse[]> {
    return await this.get<EditorAnswerResponse[]>(
      this.buildAnswersEndpoint(teachingClassId),
    );
  }

  async addUpdateAnswer(
    teachingClassId: EditorEntityId,
    annotationId: string,
    text: string,
  ): Promise<EditorAnswerResponse> {
    return await this.post<EditorAnswerResponse>(
      this.buildAnswersEndpoint(teachingClassId),
      {
        annotationId,
        answer: text,
      },
    );
  }

  async getUserClassrooms(): Promise<EditorClassroomResponse[]> {
    return await this.get<EditorClassroomResponse[]>(
      ENDPOINTS.CLASSROOM.CLASSROOMS,
    );
  }

  async linkLessonToClassroom(
    classroomId: EditorEntityId,
    lessonId: EditorEntityId,
  ): Promise<EditorClassroomResponse> {
    return await this.post<EditorClassroomResponse>(
      ENDPOINTS.CLASSROOM.LINK_LESSON_TO_CLASSROOM.replace(
        "{classroomId}",
        encodeURIComponent(String(classroomId)),
      ),
      {
        lessons: [lessonId],
      },
    );
  }

  private buildAnswersEndpoint(teachingClassId: EditorEntityId): string {
    return ENDPOINTS.EDITOR.TEACHING_CLASS_ANSWERS.replace(
      "{teachingClassId}",
      encodeURIComponent(String(teachingClassId)),
    );
  }
}

export default EditorModule;
